import { ServicesContainer } from '@/services';
import { AnalyticsDataLayerEvent, AnalyticsEventType } from '@/services/analytics/platforms/adobe-analytics/models';
import { AppState } from '@/store';
import EventEmitter from 'events';
import { Store } from 'vuex';
import * as events from './adobe-events';

declare global {
  interface Window {
    adobeDataLayer?: {
      push(event: AnalyticsDataLayerEvent): void;
      find(x: any): any;
      length: Number;
    };
  }
}

export default class AdobeAnalyticsListeners {
  static appStore: Store<AppState>;
  static services: ServicesContainer;
  static register(emitter: EventEmitter, store: Store<AppState>, services: ServicesContainer) {
    AdobeAnalyticsListeners.appStore = store;
    AdobeAnalyticsListeners.services = services;

    emitter.on('AddToBagEvent', e => this.send(new events.AdobeCartAddEvent(e, store, services)));
    emitter.on('RemoveFromBagEvent', e => this.send(new events.AdobeCartRemoveEvent(e, store, services)));
    emitter.on('OpenBagEvent', e => this.send(new events.AdobeCartOpenEvent(e, store, services)));
    emitter.on('ViewBagEvent', e => this.send(new events.AdobeCartViewEvent(e, store, services)));
    emitter.on('SubmitOrderEvent', e => this.send(new events.AdobeSubmitOrderEvent(e, store, services)));
    emitter.on('BagClickEvent', e => this.send(new events.AdobeBagClickEvent(e, store, services)));
    emitter.on('ProductViewEvent', e => this.send(new events.AdobeProductViewEvent(e, store, services)));
    emitter.on('HomePageLoadEvent', e => this.send(new events.AdobeHomePageLoadEvent(e, store, services)));
    emitter.on('LandingPageLoadEvent', e => this.send(new events.AdobeLandingPageLoadEvent(e, store, services)));
    emitter.on('CheckoutEvent', e => this.send(new events.AdobeCheckoutEvent(e, store, services)));
    emitter.on('LinkClickEvent', e => this.send(new events.AdobeLinkClickEvent(e, store, services)));
    emitter.on('ExternalCampaignEvent', e => this.send(new events.AdobeExternalCampaignEvent(e, store, services)));
    emitter.on('CarouselClickEvent', e => this.send(new events.AdobeCarouselClickEvent(e, store, services)));
    emitter.on('InternalCampaignEvent', e => this.send(new events.AdobeInternalCampaignEvent(e, store, services)));
    emitter.on('AccountPageLoadEvent', e => this.send(new events.AdobeAccountPageLoadEvent(e, store, services)));
    emitter.on('GroupOrderPageLoadEvent', e => this.send(new events.AdobeGroupOrderPageLoadEvent(e, store, services)));
    emitter.on('BagPageLoadEvent', e => this.send(new events.AdobeCartPageLoadEvent(e, store, services)));
    emitter.on('OrderReviewPageLoadEvent', e => this.send(new events.AdobeOrderReviewEvent(e, store, services)));
    emitter.on('OrderConfirmedEvent', e => this.send(new events.AdobeOrderConfirmedEvent(e, store, services)));
    emitter.on('RestaurantLocatorClickEvent', e => this.send(new events.AdobeStoreLocatorClickEvent(e, store, services)));
    emitter.on('AccountManagementEvent', e => this.send(new events.AdobeAccountManagementEvent(e, store, services)));
    emitter.on('ErrorEvent', e => this.send(new events.AdobeErrorEvent(e, store, services)));
    emitter.on('PageNotFoundEvent', () => this.send(new events.AdobePageNotFoundEvent(store, services)));
    emitter.on('SaveDeliveryAddressEvent', (e) => this.send(new events.AdobeSaveDeliveryAddressEvent(e, store, services)));
    emitter.on('NutritionLinkClickEvent', e => this.send(new events.AdobeNutritionLinkClickEvent(e, store, services)));
    emitter.on('NutritionCalculatorPageLoadEvent', e => this.send(new events.AdobeNutritionCalculatorPageLoadEvent(e, store, services)));
    emitter.on('NutritionCalculatorAddToBagEvent', e => this.send(new events.AdobeNutritionCalculatorCartAddEvent(e, store, services)));
    emitter.on('IngredientsPageLoadEvent', e => this.send(new events.AdobeIngredientsPageLoadEvent(e, store, services)));
    emitter.on('GuacModeLinkClickEvent', e => this.send(new events.AdobeGuacModeLinkClickEvent(e, store, services)));
    emitter.on('ApiErrorEvent', e => this.send(new events.AdobeApiErrorEvent(e, store, services)));
    emitter.on('TwoStepVerificationEvent', e => this.send(new events.AdobeTwoStepVerificationEvent(e, store, services)));
    emitter.on('LoginCompleteEvent', e => this.send(new events.AdobeLoginCompleteEvent(e, store, services)));
    emitter.on('LogoutCompleteEvent', e => this.send(new events.AdobeLogoutCompleteEvent()));
    emitter.on('CreateAccountPageLoadEvent', e => this.send(new events.AdobeCreateAccountPageLoadEvent(e, store, services)));
    emitter.on('PersonalizationManagementEvent', e => this.send(new events.AdobePersonalizationManagementEvent(e, store, services)));
    emitter.on('CancelOrderVisibleEvent', () => this.send(new events.AdobeCancelOrderPageLoadEvent(store, services)));
    emitter.on('CancelOrderConfirmationVisibleEvent', e => this.send(new events.AdobeCancelOrderConfirmationPageLoadEvent(e, store, services)));
    emitter.on('ExtraOptinEvent', e => this.send(new events.AdobeExtraOptinEvent(e, store, services)));
    emitter.on('SummerExtrasLeaderboardPageLoadEvent', e => this.send(new events.AdobeSummerExtrasPageLoadEvent(e, store, services)));
    emitter.on('SummerExtrasStartOrderClickEvent',    e => this.send(new events.AdobeSummerExtrasStartOrderClickEvent(e, store, services)));
    emitter.on('SummerExtrasErrorEvent', e => this.send(new events.AdobeSummerExtrasErrorEvent(e, store, services)));
    emitter.on('AlternatePickupLocationsVisibleEvent', e => this.send(new events.AdobeAlternatePickupLocationsVisibleEvent(e, store, services)));
    emitter.on('AlternatePickupConfirmationEvent', e => this.send(new events.AdobeAlternatePickupConfirmationEvent(e, store, services)));
    emitter.on('PceLandingPageLoadEvent', e => this.send(new events.AdobePceLandingPageLoadEvent(store, services)));
    emitter.on('MaxByocErrorEvent', e => this.send(new events.AdobeMaxEntreeErrorEvent(e, store, services)));
    emitter.on('ChipotleUOnboardingPageLoadEvent', e => this.send(new events.AdobeChipotleUOnboardingPageLoadEvent(e, store, services)));
    emitter.on('ChipotleUVerificationLinkClickEvent', e => this.send(new events.AdobeChipotleUVerificationLinkClickEvent(e, store, services)));
    emitter.on('RewardsEvent', e => this.send(new events.AdobeRewardsEvent(e, store, services)));
    emitter.on('RewardsPageLoadEvent', e => this.send(new events.AdobeRewardsPageLoadEvent(e, store, services)));
    emitter.on('RewardsLinkClickedEvent', e => this.send(new events.AdobeRewardsLinkClickedEvent(e, store, services)));
    // Adobe Target Events //
    emitter.on('ExperienceRequestEvent', e => this.send(new events.AdobeExperienceRequestEvent(e, store, services)));
    emitter.on('PropositionDisplayEvent', e => this.send(new events.AdobeTargetPropositionDisplayEvent(e)));
  }

  private static send(event: AnalyticsDataLayerEvent) {
    try {
      if (AdobeAnalyticsListeners.appStore.state.ui.countryProfile.isAdobeAnalyticsEnabled) {
        let isAdobeTargetEvent = event.event && ['experienceRequest', 'propositionDisplay'].includes(event.event);
        if(!isAdobeTargetEvent && !this.services.analytics.isOneEventEmpty()) {
          let combinedEvent = this.services.analytics.combineOne(event);
          window.adobeDataLayer?.push(JSON.parse(JSON.stringify(combinedEvent)));
          this.services.analytics.resetOne();
        } else {
          window.adobeDataLayer?.push(JSON.parse(JSON.stringify(event)));
        }
        this.storeLatestWebPageDetails(event)
      }
    } catch (err: any) {
      this.handleError(err);
    }
  }

  private static handleError(err: string | null): void {
    console.log(err);
  }

  private static storeLatestWebPageDetails(event: AnalyticsDataLayerEvent) {
    // Only store latest web page details if a minimum of the name was provided
    if (event.web?.webPageDetails?.name) {
      this.services.analytics.setLatestWebPageDetails(event.web.webPageDetails);
    }
  }
}
